import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import styled from 'styled-components';
import { h400, h900 } from '@atlaskit/theme/dist/cjs/typography';
import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';

const StyledButton = styled(Button)`
  text-align: center;
  display: block;
  width: 100%;
  height: 40px;
  justify-content: center;
`;

const Container = styled.div`
  min-height: 100%;
  background: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 1em;
`;

const CustomInput = styled.input`
  -moz-appearance: textfield;
  background-color: #fafbfc;
  border-color: #dfe1e6;
  border-radius: 3px;
  border-style: solid;
  border-width: 2px;
  box-sizing: border-box;
  color: #091e42;
  font-size: 14px;
  padding: 6px;
  width: 70px;
  text-align: center;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const Title = styled.div`
  ${h900};
  margin-top: 0;
`;

const Subtitle = styled.div`
  margin-bottom: 1em;
  ${h400};
`;

type Props = {
  qrCodeUrl?: string;
  onSubmit: (code: string, rememberMe: boolean) => void;
};

const EnrollmentView = ({ qrCodeUrl, onSubmit }: Props) => {
  const { t } = useTranslation('Authorization');
  const [code, setCode] = useState('');
  const [shouldRemember, setShouldRemember] = useState(false);

  useEffect(() => {
    const el = document.getElementById('mfaCode') as HTMLInputElement | null;
    el?.focus();
  }, [qrCodeUrl]);

  const onCodeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, '');
    setCode(digitsOnly.slice(0, 6));
  };

  return (
    <Container>
      {qrCodeUrl ? (
        <>
          <Title>{t('enrollmentTitle')}</Title>
          <Subtitle>{t('enrollmentSubtitle')}</Subtitle>
          <QRCode value={qrCodeUrl} />
          <p>{t('enrollmentEnterCode')}</p>
        </>
      ) : (
        <>
          <Title>{t('mfaTitle')}</Title>
          <Subtitle>{t('mfaSubtitle')}</Subtitle>
        </>
      )}

      <CustomInput
        type="text"
        id="mfaCode"
        name="mfaCode"
        value={code}
        maxLength={6}
        onChange={onCodeInputChange}
        inputMode="numeric"
        placeholder="123456"
      />
      <StyledButton appearance={'primary'} onClick={() => onSubmit(code, shouldRemember)}>
        {t(qrCodeUrl ? 'enrollmentSubmit' : 'mfaSubmit')}
      </StyledButton>
      <Checkbox
        isChecked={shouldRemember}
        label={t('mfaRememberMe') as string}
        onChange={() => setShouldRemember(!shouldRemember)}
      />
    </Container>
  );
};

export default EnrollmentView;
